/**
 * Character creation prompt templates.
 *
 * Two prompts: one for generating setting-appropriate backgrounds,
 * one for assigning GURPS stats from a character concept.
 */

/** Generate 5 setting-appropriate backgrounds. */
export function backgroundGenerationPrompt(worldSetting: string): string {
  return [
    "You are generating character backgrounds for a dark roguelike RPG.",
    "",
    `### World Setting\n${worldSetting}`,
    "",
    "### Task",
    "Generate exactly 5 backgrounds appropriate to this world setting.",
    "Each background should imply different strengths (physical, mental, social).",
    "",
    "### Required JSON Schema",
    "Return ONLY a JSON array with this exact structure:",
    "```json",
    "[",
    '  { "name": string, "description": string }',
    "]",
    "```",
    "- `name`: 2-3 words, evocative",
    "- `description`: 1-2 sentences in 2nd person describing who you were before this adventure began",
    "",
    "### Examples",
    '{"name":"Disgraced Knight","description":"You once served a lord who fell to madness. Your sword arm is strong, but your reputation is ruined."}',
    '{"name":"Hedge Witch","description":"You learned forbidden remedies from the old woman in the marsh. Your knowledge of herbs and hexes keeps you alive."}',
    '{"name":"Sewer Rat","description":"You grew up in the tunnels beneath the city. You know every drain, every grate, every hiding spot the guards never check."}',
    "",
    "### Rules",
    "- Descriptions: 2nd person, present tense, gritty tone",
    "- Each should suggest different GURPS attribute strengths (ST/DX/IQ/HT)",
    "- Setting-appropriate — no anachronisms",
    "- Return ONLY the JSON array, no other text",
  ].join("\n");
}

/** Assign GURPS stats from a character concept. */
export function characterAssignmentPrompt(
  worldSetting: string,
  background: string,
  archetype: string,
): string {
  return [
    "You are a GURPS character creation assistant for a dark roguelike RPG.",
    "",
    "### Context",
    `- **World Setting**: ${worldSetting}`,
    `- **Background**: ${background}`,
    `- **Player's Character Concept**: ${archetype}`,
    "",
    "### Task",
    "Assign GURPS attributes and skills based on the character concept.",
    "",
    "### GURPS Rules",
    "- Primary attributes: ST, DX, IQ, HT — each 7-14, total 40-44",
    "- 10 is average human. 14 is exceptional. 7 is below average.",
    "- Assign 8-12 skill points across 3-5 skills from this registry:",
    "  Sword, Axe/Mace, Bow, Shield, Stealth, Lockpicking, Climbing,",
    "  Swimming, First Aid, Survival, Tracking, Diplomacy, Intimidation,",
    "  Streetwise, Merchant, Occultism, Alchemy, Naturalist, Observation,",
    "  Traps, Thrown Weapon, Wrestling, Running",
    "- Skills should match the character concept and background",
    "- Higher points = more proficient (1-2 = familiar, 4+ = skilled)",
    "",
    "### Required JSON Schema",
    "Return ONLY a JSON object with this exact structure:",
    "```json",
    "{",
    '  "name": string,        // setting-appropriate, 1-3 words',
    '  "st": number,          // 7-14',
    '  "dx": number,          // 7-14',
    '  "iq": number,          // 7-14',
    '  "ht": number,          // 7-14',
    '  "skills": { string: number },  // SkillName: points',
    '  "rationale": string    // 1-2 sentences explaining fit',
    "}",
    "```",
    "",
    "### Example Output",
    '{"name":"Gideon Ashfall","st":12,"dx":10,"iq":9,"ht":11,"skills":{"Sword":4,"Shield":2,"Survival":3,"Intimidation":2},"rationale":"A former soldier built for endurance and close combat. His time in the wilds taught him to survive off the land."}',
    "",
    "Return ONLY the JSON object, no other text.",
  ].join("\n");
}
